import { Listener, ListenerSettings } from '@/pages/shop/listeners/listener';
import {
    CartItemChildType,
    Event,
    OpenTicketShop,
    Ticket,
} from '@openticket/lib-shop';
import { CustomShopSettingsDynamicListener } from '@openticket/lib-custom-shop-settings';
import { isNonEmptyCollection } from '@/utils/emptyCollection';
import { eventPassesFilter, ticketPassesFilter } from '@/utils/contentFilters';
import { ShopModuleShopContentModifiers } from '@/pages/shop/modules/module';

interface ListenerForceCartItemSettings extends ListenerSettings {
    force?: {
        // Ids of tickets to force when a product in the on prop is added
        ids?: string[];
        // Only one of these ids of tickets should be force when a product in the on prop is added.
        // If none are present yet, the first is picked.
        oneOfIds?: string[];
        names?: string[];
        coupon?: string;
        on?: {
            all?: boolean;
            ids?: string[];
            names?: string[];
        };
    };
}

type FoundWithTicketAndEvent =
    | { found: true; event: Event; ticket: Ticket }
    | { found: false };

function compareItemMatchingListenerSpec(
    item: { guid: string; name: string },
    spec?: { ids?: string[]; names?: string[] }
): boolean {
    const lcName = item.name.toLowerCase();

    return (
        spec?.ids?.includes(item.guid) ||
        spec?.names?.some((criteria: string) =>
            lcName.includes(criteria.toLowerCase())
        ) ||
        false
    );
}

/**
 * The forced cart listener allows users to configure a listener that compels tickets to be added to a cart when
 * specific (or any) tickets in the shop are added. Upon removal of these "forced" tickets, all items associated
 * with them are also removed.
 *
 * (*) If multiple ids are forced they will all be required. Therefor if one is removed, all forced on ids are also
 * removed from cart
 *
 * (*) If you supply multiple forced listeners, make sure the all ids are unique between them to prevent unexpected and
 * unintended behavior
 */
export class ListenerForceCartItem extends Listener {
    static ownName = 'force-cart';

    private readonly cartListener?: string;
    private settings: ListenerForceCartItemSettings | null = null;
    private shopContentModifiers: {
        state: ShopModuleShopContentModifiers | null;
    };
    private readonly notify: (slug: string, data: object) => void;

    constructor(
        shop: OpenTicketShop,
        settings: CustomShopSettingsDynamicListener,
        shopContentModifiers: { state: ShopModuleShopContentModifiers | null },
        notify: (slug: string, data: object) => void = () => {
            /* No-op */
        }
    ) {
        super(shop);

        this.shopContentModifiers = shopContentModifiers;
        this.notify = notify;

        if (!this.validateSettings(settings)) {
            return;
        }
        this.settings = settings as ListenerForceCartItemSettings;

        this.cartListener = this.shop.events.on(
            ['cart', 'ticket'],
            async (path: string[], data: any) => {
                if (path.length > 3) {
                    switch (path[3]) {
                        case 'add':
                            if (
                                this.isForcedOnCartTicket(data.guid, data.name)
                            ) {
                                await this.forceCouponCode();
                                await this.forceCartTickets();
                            }
                            break;
                        case 'remove':
                            if (
                                (this.isForcedCartTicket(
                                    data.guid,
                                    data.name
                                ) ||
                                    this.isLastOneOfCartTicket(data.guid)) &&
                                data.count < 1
                            ) {
                                await this.removeForcedOnCartTicket();
                            }
                            break;
                    }
                }
            }
        );
    }

    validateSettings(settings: CustomShopSettingsDynamicListener): boolean {
        if (
            !settings ||
            !(settings as ListenerForceCartItemSettings).force ||
            ((!(settings as ListenerForceCartItemSettings).force?.ids ||
                !(settings as ListenerForceCartItemSettings)?.force?.ids
                    ?.length) &&
                (!(settings as ListenerForceCartItemSettings).force?.oneOfIds ||
                    !(settings as ListenerForceCartItemSettings)?.force
                        ?.oneOfIds?.length) &&
                (!(settings as ListenerForceCartItemSettings).force?.names ||
                    !(settings as ListenerForceCartItemSettings)?.force?.names
                        ?.length) &&
                (!(settings as ListenerForceCartItemSettings).force?.coupon ||
                    !(settings as ListenerForceCartItemSettings)?.force?.coupon
                        ?.length))
        ) {
            // @todo: add warning to logger
            console.warn(
                'ListenerForceCart: Invalid settings, force cart item listener should have a forced id. Ignoring listener'
            );
            return false;
        }
        if (
            !settings ||
            !(settings as ListenerForceCartItemSettings).force ||
            !(settings as ListenerForceCartItemSettings).force?.on ||
            ((!(settings as ListenerForceCartItemSettings)?.force?.on?.ids ||
                !(settings as ListenerForceCartItemSettings)?.force?.on?.ids
                    ?.length) &&
                (!(settings as ListenerForceCartItemSettings)?.force?.on
                    ?.names ||
                    !(settings as ListenerForceCartItemSettings)?.force?.on
                        ?.names?.length) &&
                !(settings as ListenerForceCartItemSettings)?.force?.on?.all)
        ) {
            // @todo: add warning to logger
            console.warn(
                'ListenerForceCart: Invalid settings, force cart item listener should have a force on setting. Ignoring listener'
            );
            return false;
        }

        if (
            (settings as ListenerForceCartItemSettings)?.force?.on?.ids &&
            (settings as ListenerForceCartItemSettings)?.force?.on?.ids
                ?.length &&
            (settings as ListenerForceCartItemSettings)?.force?.on?.all
        ) {
            // @todo: add warning to logger
            console.warn(
                'ListenerForceCart: Force on all is set, the ids property will be ignored'
            );
        }

        if (
            (settings as ListenerForceCartItemSettings)?.force?.on?.names &&
            (settings as ListenerForceCartItemSettings)?.force?.on?.names
                ?.length &&
            (settings as ListenerForceCartItemSettings)?.force?.on?.all
        ) {
            // @todo: add warning to logger
            console.warn(
                'ListenerForceCart: Force on all is set, the names property will be ignored'
            );
        }

        return true;
    }

    ticketIsInCart(eventId: string, guid: string): boolean {
        return isNonEmptyCollection(
            this.shop.cart.items[eventId]?.[CartItemChildType.Tickets].map[guid]
        );
    }

    ticketIsInShopData(guid: string): FoundWithTicketAndEvent {
        const ticket: Ticket | undefined = this.shop.data.tickets_map[guid];

        if (!ticket) {
            return { found: false };
        }

        const event: Event | undefined = this.shop.data.events_map[
            ticket.event_id
        ];

        if (!event) {
            return { found: false };
        }

        return {
            found: true,
            event,
            ticket,
        };
    }

    ticketPassesFilterContentModifiers(event: Event, ticket: Ticket): boolean {
        if (!this.shopContentModifiers.state) {
            return true;
        }

        return (
            eventPassesFilter(this.shopContentModifiers.state, event) &&
            ticketPassesFilter(this.shopContentModifiers.state, ticket)
        );
    }

    // Check if the supplied guid is part of the forced set
    isForcedCartTicket(guid: string, name: string): boolean {
        return compareItemMatchingListenerSpec(
            {
                guid,
                name,
            },
            this.settings?.force
        );
    }

    isOneOfCartTicket(guid: string): boolean {
        return this.settings?.force?.oneOfIds?.includes(guid) || false;
    }

    isLastOneOfCartTicket(guid: string): boolean {
        if (!this.isOneOfCartTicket(guid)) {
            return false;
        }

        return (
            this.settings?.force?.oneOfIds?.every((ticketId: string) => {
                if (ticketId === guid) {
                    // Ignore an exact match, this one can still be present to satisfy the comparison.
                    return true;
                }

                const isInShopDataResult: FoundWithTicketAndEvent = this.ticketIsInShopData(
                    ticketId
                );

                if (!isInShopDataResult.found) {
                    // Tickets that are not in the shop data do not matter for the check,
                    // therefore should not be counted in the 'every' call.
                    return true;
                }

                // When any ticket, part of the oneOf group IS in the cart,
                // but not the argument (guid), the argument (guid) is clearly not the last one.
                return !this.ticketIsInCart(
                    isInShopDataResult.event.guid,
                    ticketId
                );
            }) || false
        );
    }

    // Check if the supplied guid is part of the forced on set
    isForcedOnCartTicket(guid: string, name: string): boolean {
        // Forced (or oneOf) ticket cant also be a forced on ticket
        if (
            this.isForcedCartTicket(guid, name) ||
            this.isOneOfCartTicket(guid)
        ) {
            return false;
        }

        if (this.settings?.force?.on?.all) {
            return true;
        }

        return compareItemMatchingListenerSpec(
            {
                guid,
                name,
            },
            this.settings?.force?.on
        );
    }

    // Checks if the forced tickets are in cart, if not add them (but only if they exist in the shop)
    async forceCartTickets(): Promise<void> {
        if (
            !this.settings?.force ||
            (!this.settings?.force?.ids &&
                !this.settings?.force?.oneOfIds &&
                !this.settings?.force?.names)
        ) {
            return;
        }

        const { ids, oneOfIds, names } = this.settings.force;

        const ticketsToAdd: Set<string> = new Set();

        if (ids) {
            for (const ticketId of ids) {
                const isInShopDataResult: FoundWithTicketAndEvent = this.ticketIsInShopData(
                    ticketId
                );

                if (!isInShopDataResult.found) {
                    continue;
                }

                if (
                    // Ignore hidden tickets
                    isInShopDataResult.ticket.hidden ||
                    // Ignore filtered out tickets
                    !this.ticketPassesFilterContentModifiers(
                        isInShopDataResult.event,
                        isInShopDataResult.ticket
                    ) ||
                    // Ignore tickets that are already in the cart
                    this.ticketIsInCart(isInShopDataResult.event.guid, ticketId)
                ) {
                    continue;
                }

                ticketsToAdd.add(ticketId);
            }
        }

        if (oneOfIds && oneOfIds.length) {
            let firstId: string | null = null;

            const cartHasAtLeastOne = oneOfIds.some((ticketId: string) => {
                const isInShopDataResult: FoundWithTicketAndEvent = this.ticketIsInShopData(
                    ticketId
                );

                if (!isInShopDataResult.found) {
                    return false;
                }

                if (
                    this.ticketIsInCart(isInShopDataResult.event.guid, ticketId)
                ) {
                    return true;
                }

                if (
                    !firstId &&
                    !isInShopDataResult.ticket.hidden &&
                    this.ticketPassesFilterContentModifiers(
                        isInShopDataResult.event,
                        isInShopDataResult.ticket
                    ) &&
                    !this.ticketIsInCart(
                        isInShopDataResult.event.guid,
                        ticketId
                    )
                ) {
                    // Set the firstId when this ticket is in a state that makes it eligible to add to the cart.
                    firstId = ticketId;
                }

                return false;
            });

            if (!cartHasAtLeastOne && firstId) {
                ticketsToAdd.add(firstId);
            }
        }

        if (names) {
            // All tickets in the shop data need to be validated against the spec
            for (const ticketId of Object.keys(this.shop.data.tickets_map)) {
                const isInShopDataResult: FoundWithTicketAndEvent = this.ticketIsInShopData(
                    ticketId
                );

                if (!isInShopDataResult.found) {
                    continue;
                }

                const isForced: boolean = compareItemMatchingListenerSpec(
                    isInShopDataResult.ticket,
                    this.settings?.force
                );

                if (
                    !isForced ||
                    isInShopDataResult.ticket.hidden ||
                    !this.ticketPassesFilterContentModifiers(
                        isInShopDataResult.event,
                        isInShopDataResult.ticket
                    ) ||
                    this.ticketIsInCart(isInShopDataResult.event.guid, ticketId)
                ) {
                    continue;
                }

                ticketsToAdd.add(ticketId);
            }
        }

        await [...ticketsToAdd].reduce(
            async (
                previous: Promise<void>,
                ticketId: string
            ): Promise<void> => {
                await previous;
                try {
                    await this.shop.cart.addTicket(ticketId);
                } catch (e) {
                    this.notifyError(
                        e,
                        'shop.notifications.listeners.force_cart.error.ticket.add'
                    );
                }
            },
            Promise.resolve()
        );
    }

    // Checks if the forced coupon is in the cart, if not, try to add it.
    async forceCouponCode(): Promise<void> {
        if (
            !this.settings?.force?.coupon ||
            Object.keys(this.shop.cart.getAppliedCoupons()).includes(
                this.settings.force.coupon
            )
        ) {
            return;
        }

        try {
            await this.shop.cart.addCoupon(this.settings.force.coupon);
        } catch (e) {
            this.notifyError(
                e,
                'shop.notifications.listeners.force_cart.error.coupon.add'
            );
        }
    }

    async removeForcedOnCartTicket(): Promise<void> {
        if (this.settings?.force?.on?.all) {
            void this.shop.cart.removeAllItems();

            return;
        }

        if (
            !this.settings?.force?.on?.ids &&
            !this.settings?.force?.on?.names
        ) {
            return;
        }

        await Object.entries(
            this.shop.cart.flatItems[CartItemChildType.Tickets]
        ).reduce(async (previous: Promise<void>, [key, ticket]): Promise<
            void
        > => {
            await previous;

            if (
                !this.isForcedOnCartTicket(ticket.item.guid, ticket.item.name)
            ) {
                return previous;
            }

            await previous;
            try {
                await this.shop.cart.removeTicket(ticket.item.guid, key);
            } catch (e) {
                this.notifyError(
                    e,
                    'shop.notifications.listeners.force_cart.error.ticket.remove'
                );
            }
        }, Promise.resolve());
    }

    destroy(): void {
        if (this.cartListener) {
            this.shop.off(this.cartListener);
        }
    }

    private notifyError(
        e: unknown,
        defaultSlug = 'shop.notifications.listeners.force_cart.error.generic'
    ): void {
        if (
            e &&
            typeof e === 'object' &&
            '_isOpenTicketLogMessage' in e &&
            e._isOpenTicketLogMessage
        ) {
            if (
                'slug' in e &&
                e.slug &&
                typeof e.slug === 'string' &&
                (!('data' in e) || !e.data || typeof e.data === 'object')
            ) {
                this.notify(e.slug, 'data' in e ? e.data || {} : {});

                return;
            }

            if (
                'data' in e &&
                e.data &&
                typeof e.data === 'object' &&
                'exception' in e.data &&
                e.data.exception &&
                typeof e.data.exception === 'object' &&
                'slug' in e.data.exception &&
                e.data.exception.slug &&
                typeof e.data.exception.slug === 'string'
            ) {
                this.notify(e.data.exception.slug, e.data.exception);
                return;
            }
        }

        this.notify(defaultSlug, {});
    }
}
